import { BUSINESS_TIME_ZONE, Decimal } from '@paguz/contracts';

/**
 * Presentación (es-CO). Solo da formato a lo que manda la API: no calcula cifras financieras.
 * Montos: usa `MoneyText`/`formatCop` de `@paguz/ui`.
 */

const dateFormatter = new Intl.DateTimeFormat('es-CO', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});
const shortDateFormatter = new Intl.DateTimeFormat('es-CO', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
const longDateFormatter = new Intl.DateTimeFormat('es-CO', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});
const dateTimeFormatter = new Intl.DateTimeFormat('es-CO', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: BUSINESS_TIME_ZONE,
});

function businessDateToUtc(date: string): Date {
  return new Date(`${date}T00:00:00.000Z`);
}

function parts(formatter: Intl.DateTimeFormat, date: Date): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of formatter.formatToParts(date)) out[part.type] = part.value.replace(/\./g, '');
  return out;
}

/** `2026-11-20` → `20 nov 2026`. */
export function formatDate(date: string): string {
  const p = parts(dateFormatter, businessDateToUtc(date));
  return `${p.day ?? ''} ${p.month ?? ''} ${p.year ?? ''}`;
}

/** `2026-11-20` → `20 nov`. */
export function formatShortDate(date: string): string {
  const p = parts(shortDateFormatter, businessDateToUtc(date));
  return `${p.day ?? ''} ${p.month ?? ''}`;
}

/** `2026-11-20` → `viernes, 20 de noviembre`. */
export function formatLongDate(date: string): string {
  return longDateFormatter.format(businessDateToUtc(date));
}

/** Instante ISO → `30 oct 2026, 11:10 a. m.` (hora de Colombia). */
export function formatDateTime(iso: string): string {
  const p = parts(dateTimeFormatter, new Date(iso));
  const period = (p.dayPeriod ?? '').replace(/\s+/g, ' ').replace(/^(a|p) m$/, '$1. m.');
  return `${p.day ?? ''} ${p.month ?? ''} ${p.year ?? ''}, ${p.hour ?? ''}:${p.minute ?? ''} ${period}`.trim();
}

/** Tasa como fracción (`"0.452100"`) → `45,21 %`. */
export function formatRate(rate: string, decimals = 2): string {
  try {
    return `${new Decimal(rate).times(100).toFixed(decimals).replace('.', ',')} %`;
  } catch {
    return '—';
  }
}

/** Celular `+573001112233` → `300 111 2233`. */
export function formatPhone(phone: string): string {
  const local = phone.replace(/^\+57/, '');
  return local.length === 10
    ? `${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`
    : phone;
}

/** Primer nombre para saludos. */
export function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? fullName;
}
